import { jwtDecode } from 'jwt-decode';

/**
 * Token Validator Utility
 * Provides client-side JWT token validation functions
 */

/**
 * Safely decode a JWT token
 * @param {string} token - JWT token string
 * @returns {object|null} Decoded token payload or null if invalid
 */
export function decodeToken(token) {
  if (!token || typeof token !== 'string') {
    return null;
  }

  try {
    return jwtDecode(token);
  } catch (error) {
    console.error('[TokenValidator] Token decode error:', error.message);
    return null;
  }
}

/**
 * Get token expiration timestamp
 * @param {string} token - JWT token string
 * @returns {number|null} Expiration timestamp (seconds since epoch) or null if invalid
 */
export function getTokenExpiration(token) {
  const decoded = decodeToken(token);

  if (!decoded || !decoded.exp) {
    return null;
  }

  return decoded.exp;
}

/**
 * Check if token is valid (exists and not expired)
 * @param {string} token - JWT token string
 * @returns {boolean} True if token is valid and not expired
 */
export function isTokenValid(token) {
  if (!token) {
    return false;
  }

  const exp = getTokenExpiration(token);

  if (!exp) {
    return false;
  }

  // Token is valid if expiration is in the future
  const now = Math.floor(Date.now() / 1000);
  return exp > now;
}

/**
 * Check if token is expiring soon
 * @param {string} token - JWT token string
 * @param {number} minutesThreshold - Minutes before expiration to trigger warning (default: 5)
 * @returns {boolean} True if token expires within the threshold
 */
export function isTokenExpiringSoon(token, minutesThreshold = 5) {
  if (!token) {
    return false;
  }

  const exp = getTokenExpiration(token);

  if (!exp) {
    return false;
  }

  const now = Math.floor(Date.now() / 1000);
  const thresholdSeconds = minutesThreshold * 60;
  const timeUntilExpiry = exp - now;

  // Token is expiring soon if time until expiry is less than threshold but still positive
  return timeUntilExpiry > 0 && timeUntilExpiry <= thresholdSeconds;
}

/**
 * Get time remaining until token expires (in seconds)
 * @param {string} token - JWT token string
 * @returns {number|null} Seconds until expiration or null if invalid/expired
 */
export function getTimeUntilExpiry(token) {
  if (!token) {
    return null;
  }

  const exp = getTokenExpiration(token);

  if (!exp) {
    return null;
  }

  const now = Math.floor(Date.now() / 1000);
  const timeRemaining = exp - now;

  // Return null if already expired
  return timeRemaining > 0 ? timeRemaining : null;
}

export default {
  decodeToken,
  getTokenExpiration,
  isTokenValid,
  isTokenExpiringSoon,
  getTimeUntilExpiry
};
